import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { ReactiveFormsModule, FormsModule, FormControl } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Observable, Subscription } from 'rxjs';
import { APIService } from '../../fleet-management/backend-service/backend-service';
import { GlobalVariablesService } from '../../global.service';
import { SettingService } from '../setting-service';
import { SettingData } from '../../models/settings.model';
import { NotificationService } from '../../shared/services/notification.service';
import { ConfirmationDialogService } from '../../shared/services/confirmation-dialog.service';
import { LoadingService } from '../../services/loading.service';

@Component({
  selector: 'app-customer-settings',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatSelectModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './customer-settings.component.html',
  styleUrl: './customer-settings.component.scss'
})
export class CustomerSettingsComponent implements OnDestroy {
  searchControl = new FormControl('');
  businessTypes: SettingData[] = [];
  creditTerms: SettingData[] = [];
  financialRatings: SettingData[] = [];
  filteredOptions!: Observable<string[]>;
  isLoading: boolean = false;
  private loadingSubscription: Subscription;

  constructor(private apiService: APIService, private globalVariable: GlobalVariablesService, private settingService: SettingService,
    private notification: NotificationService, private confirmationDialog: ConfirmationDialogService,
    public loadingService: LoadingService) {
    this.loadingSubscription = this.loadingService.isLoading$.subscribe(loading => this.isLoading = loading);
  }

  ngOnDestroy() {
    if (this.loadingSubscription) {
      this.loadingSubscription.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.getSettingsFromAPI();
    this.setBusinessType();
    this.setCreditTerms();
    this.setFinancialRatingss();
  }

  getSettingsFromAPI(): void {
    this.settingService.getSettings().subscribe({
      next: (response) => {
        console.log('Settings loaded successfully:', response);
        this.setBusinessType();
        this.setCreditTerms();
        this.setFinancialRatingss();
      },
      error: (error) => {
        console.error('Error loading settings:', error);
      }
    });
  }
  setBusinessType() {
    for (const role of this.globalVariable.SETTING_BUSINESS_TYPES || []) {
      if (!this.businessTypes.some(r => r["id"] === role.id)) {
        this.businessTypes.push(role);
      }
    }
  }
  setCreditTerms() {
    for (const role of this.globalVariable.SETTING_CREDIT_TERMS || []) {
      if (!this.creditTerms.some(r => r["id"] === role.id)) {
        this.creditTerms.push(role);
      }
    }
  }
  setFinancialRatingss() {
    for (const role of this.globalVariable.SETTING_FINANCIAL_RATINGS || []) {
      if (!this.financialRatings.some(r => r["id"] === role.id)) {
        this.financialRatings.push(role);
      }
    }
  }

  addNewSetting(roleName: string, endpoint: string): void {
    let loginData = JSON.parse(localStorage.getItem('loginData')!)
    let jsonData = {
      "name": roleName,
    }
    console.log("Setting data: ", jsonData)
    this.apiService.post(endpoint, jsonData, loginData.token)
      .subscribe({
        next: (response) => {
          console.log('HTTP Status:', response.status); // 👈 Get status here
          console.log('Response Body:', response.body);
          if (response.status === 200 || response.status === 201) {
            this.getSettingsFromAPI();
            this.notification.success('Add New Setting successfully');

          }
          else {
            this.notification.error('Add New Setting failed. Please try again.');
          }
        },
        error: (err) => {
          console.error('Error Status:', err.status);    // 👈 Also available on error
          console.error('Error Message:', err.error);
        }
      });
  }
  deleteSetting(settingId: String, endpoint: string): void {
    let loginData = JSON.parse(localStorage.getItem('loginData')!)
    this.apiService.delete(endpoint + settingId, loginData.token).subscribe({
      next: (response) => {
        console.log('Status:', response.status);
        console.log(endpoint + ',\n' + 'Delete_Setting_Response:', response);
        if (response.status === 200) {
          this.getSettingsFromAPI();
        }
        this.notification.success(response.message || 'Operation completed successfully');

      },
      error: (error) => {
        console.error('Error status:', error.status);
        console.error('Error message:', error.message);
      }
    });
  }


  addBusinessType(): void {
    const value = this.searchControl.value?.trim();
    if (value && !this.businessTypes.some(role => role.name === value)) {
      this.addNewSetting(value, '/api/predefined-data/BUSINESS_TYPES');
    }
    this.searchControl.setValue('');
  }
  removeBusinessType(item: SettingData) {
    this.confirmationDialog.confirmDelete(item.name).subscribe(confirmed => {
      if (confirmed) {
        this.businessTypes = this.businessTypes.filter(i => i.id !== item.id);
        this.deleteSetting(item.id, '/api/predefined-data/BUSINESS_TYPES/');
      }
    });
  }

  addCreditTerms(): void {
    const value = this.searchControl.value?.trim();
    if (value && !this.creditTerms.some(role => role.name === value)) {
      this.addNewSetting(value, '/api/predefined-data/CREDIT_TERMS');
    }
    this.searchControl.setValue('');
  }
  removeCreditTerms(item: SettingData) {
    this.confirmationDialog.confirmDelete(item.name).subscribe(confirmed => {
      if (confirmed) {
        this.creditTerms = this.creditTerms.filter(i => i.id !== item.id);
        this.deleteSetting(item.id, '/api/predefined-data/CREDIT_TERMS/');
      }
    });
  }

  addFinancialRating(): void {
    const value = this.searchControl.value?.trim();
    if (value && !this.financialRatings.some(role => role.name === value)) {
      this.addNewSetting(value, '/api/predefined-data/FINANCIAL_RATINGS');
    }
    this.searchControl.setValue('');
  }
  removeFinancialRating(item: SettingData) {
    this.confirmationDialog.confirmDelete(item.name).subscribe(confirmed => {
      if (confirmed) {
        this.financialRatings = this.financialRatings.filter(i => i.id !== item.id);
        this.deleteSetting(item.id, '/api/predefined-data/FINANCIAL_RATINGS/');
      }
    });
  }
}
